import useTranslation from 'next-translate/useTranslation';

// Utils
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import { useUserGroupRestriction } from '@/utils/hooks/acl/useUserGroupRestriction';

// Components
import TabsPopover from '@/components/base/Tabs/TabsPopover';
import { PopoverWrapper, PopoverContainer } from '@/components/base/Popover';
import { GroupsTab } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/GroupsTab';
import { ProfilesTab } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/ProfilesTab';
import { Footer } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/Footer';
import { RecapSocialProfiles } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/RecapSocialProfiles';
import { TooltipPlanRestrictionContent } from '@/components/layouts/LayoutPlatform/components/Menu/components/TooltipPlanRestriction';

// Types
import type { TabsPopoverProps } from '@/components/base/Tabs/TabsPopover';

export function ManageProfiles() {
    const { t } = useTranslation('layout_platform');
    const { activatedSocialProfiles } = useSocialProfiles({ revalidateIfStale: false });
    const { activatedSpGroups } = useSPGroups({ revalidateIfStale: false });
    const { hasGroupPermission } = useUserGroupRestriction();

    const nbProfiles = activatedSocialProfiles?.length || 0;
    const nbGroups = activatedSpGroups?.length || 0;

    const tabs: TabsPopoverProps['tabs'] = [
        {
            label: t('manage_profiles.tabs.all_profiles', { count: nbProfiles }),
            icon: {
                active: 'single-profile-on',
                inactive: 'single-profile'
            }
        },
        {
            label: t('manage_profiles.tabs.groups', { count: nbGroups }),
            icon: {
                active: 'group-on',
                inactive: 'group'
            },
            disabled: !hasGroupPermission,
            tooltip: {
                appendTo: document.body,
                interactive: true,
                disabled: hasGroupPermission,
                content: <TooltipPlanRestrictionContent />
            }
        }
    ];

    return (
        <PopoverWrapper
            popperOptions={{
                modifiers: [
                    {
                        name: 'flip',
                        options: {
                            fallbackPlacements: ['auto']
                        }
                    }
                ]
            }}>
            {({ setReferenceElement, setPopperElement, styles, attributes }) => (
                <>
                    <RecapSocialProfiles setReferenceElement={setReferenceElement} />
                    <PopoverContainer
                        setPopperElement={setPopperElement}
                        styles={styles}
                        attributes={attributes}>
                        <TabsPopover tabs={tabs}>
                            <ProfilesTab />
                            <GroupsTab />
                        </TabsPopover>
                        <Footer />
                    </PopoverContainer>
                </>
            )}
        </PopoverWrapper>
    );
}
